/**
 * Cross-vertical role availability & validation.
 *
 * The two-dimensional RBAC matrix (organization type × role) resolved across
 * the club and school verticals. Replaces the former core/permissions/rbac.ts —
 * the role lists now come from each vertical's source of truth.
 */
import { UserRole } from '../../../types/user';
import { OrganizationType } from '../../../models/Organization';
import { CLUB_ROLES } from '../../club/permissions';
import { SCHOOL_ROLES } from '../../school/permissions';

/**
 * Roles available for an organization type (the vertical's role list).
 * `maintenance` is intentionally excluded from both until fully supported.
 */
export function getRolesForOrg(organizationType: OrganizationType): UserRole[] {
  return organizationType === 'club' ? [...CLUB_ROLES] : [...SCHOOL_ROLES];
}

// Alias kept for API compatibility with the previous rbac module.
export const getAvailableRoles = getRolesForOrg;

export function isRoleAvailableForOrganization(
  role: UserRole,
  organizationType: OrganizationType
): boolean {
  return getRolesForOrg(organizationType).includes(role);
}

// Get role display info based on organization context
export function getRoleInfo(
  role: UserRole,
  organizationType: OrganizationType
): {
  name: string;
  fullName: string;
  description: string;
  available: boolean;
} {
  const available = isRoleAvailableForOrganization(role, organizationType);

  const roleInfo: Record<UserRole, { name: string; fullName: string; description: string }> = {
    admin: {
      name: 'Admin',
      fullName: 'Administrator',
      description: 'Full administrative access to organization and all features',
    },
    member: {
      name: 'Member',
      fullName: 'Member Pilot',
      description: organizationType === 'club'
        ? 'Club member with booking and flight privileges'
        : 'Standard pilot access for booking flights and managing profile',
    },
    cfi: {
      name: 'CFI',
      fullName: 'Certified Flight Instructor',
      description: 'Can instruct students, approve flight plans, and manage training progress',
    },
    student: {
      name: 'Student',
      fullName: 'Student Pilot',
      description: 'Training access for scheduling lessons and tracking progress',
    },
    dispatch: {
      name: 'Dispatch',
      fullName: 'Flight Dispatcher',
      description: 'Can coordinate flights, manage scheduling, and assign resources',
    },
    maintenance: {
      name: 'Maintenance',
      fullName: 'Maintenance Personnel',
      description: 'Can manage aircraft maintenance, squawks, and work orders',
    },
  };

  return {
    ...roleInfo[role],
    available,
  };
}

// Validate that a user's role is appropriate for the organization
export function validateUserRole(
  role: UserRole,
  organizationType: OrganizationType
): { valid: boolean; error?: string } {
  if (!isRoleAvailableForOrganization(role, organizationType)) {
    const orgTypeDisplay = organizationType === 'club'
      ? 'Flying Club'
      : 'Flight School';

    return {
      valid: false,
      error: `Role "${role}" is not available for ${orgTypeDisplay} organizations`,
    };
  }

  return { valid: true };
}

// Helper to get organization type display name
export function getOrganizationTypeDisplayName(type: OrganizationType): string {
  const names: Record<OrganizationType, string> = {
    club: 'Flying Club',
    school: 'Flight School',
  };
  return names[type] || type;
}

// Get a formatted matrix of roles and their availability by organization type
export function getRoleAvailabilityMatrix(): Record<
  OrganizationType,
  Record<UserRole, boolean>
> {
  const allRoles: UserRole[] = ['admin', 'member', 'cfi', 'student', 'dispatch', 'maintenance'];
  const orgTypes: OrganizationType[] = ['club', 'school'];

  const matrix: Record<OrganizationType, Record<UserRole, boolean>> = {} as any;

  orgTypes.forEach(orgType => {
    matrix[orgType] = {} as Record<UserRole, boolean>;
    allRoles.forEach(role => {
      matrix[orgType][role] = isRoleAvailableForOrganization(role, orgType);
    });
  });

  return matrix;
}

// Migration helper: suggests correct role when user has invalid role for org type
export function suggestAlternativeRole(
  currentRole: UserRole,
  organizationType: OrganizationType
): UserRole | null {
  // If role is already valid, no suggestion needed
  if (isRoleAvailableForOrganization(currentRole, organizationType)) {
    return null;
  }

  // Mapping of invalid roles to suggested alternatives
  const suggestions: Record<OrganizationType, Partial<Record<UserRole, UserRole>>> = {
    club: {
      cfi: 'member', // CFI in club becomes member
      student: 'member', // Student in club becomes member
      dispatch: 'admin', // Dispatch in club becomes admin
    },
    school: {
      member: 'student', // Member in school becomes student (or could be CFI)
    },
  };

  return suggestions[organizationType]?.[currentRole] || 'member';
}

// Comprehensive validation for user assignment
export interface UserRoleValidation {
  valid: boolean;
  role: UserRole;
  organizationType: OrganizationType;
  error?: string;
  suggestedRole?: UserRole;
  warnings?: string[];
}

export function validateUserRoleAssignment(
  role: UserRole,
  organizationType: OrganizationType
): UserRoleValidation {
  const validation = validateUserRole(role, organizationType);

  if (!validation.valid) {
    const suggested = suggestAlternativeRole(role, organizationType);
    return {
      valid: false,
      role,
      organizationType,
      error: validation.error,
      suggestedRole: suggested || undefined,
    };
  }

  return {
    valid: true,
    role,
    organizationType,
  };
}
